import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Typography } from '@mui/material';

import { useBulkSelect } from '@occc-app/hooks/useBulkSelect';
import { SearchForm, TableContainer, TableHeader } from '@occc-app/design-system';
import { pluralize } from '@occc-app/lib/strings';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { useSearchParams } from '@occc-app/hooks/useSearchParams';
import { Cpg } from '@occc/graphql';

import { SubmissionGroupLocationsTable } from './submission-group-locations-table';

import { useGetSubmissionGroupLocations } from '../../../../hooks/use-get-submission-group-locations.hook';

type SubmissionGroupLocationsTableControllerProps = ReturnType<typeof useBulkSelect> & {
  cpg: Cpg;
};

export const SubmissionGroupLocationsTableController = ({
  cpg,
  bulkSelectedItems,
  handleBulkSelectMultiple,
  handleBulkDeselectAll,
  handleItemSelect,
}: SubmissionGroupLocationsTableControllerProps) => {
  const prevCpgRef = useRef(cpg);
  useEffect(() => {
    if (prevCpgRef.current !== cpg) {
      prevCpgRef.current = cpg;
      handleBulkDeselectAll();
    }
  }, [cpg, handleBulkDeselectAll]);

  const [search, setSearch] = useSearchParams();

  const [paginationInfo, onPageChange] = usePaginationParams();

  const { data, loading, error, paginationProps } = useGetSubmissionGroupLocations({
    search,
    paginationInfo,
    onPageChange,
  });

  const tableActions = useMemo(
    () => <SearchForm onSubmit={({ search }) => setSearch(search)} />,
    [setSearch],
  );

  const selectedCount = bulkSelectedItems.size;

  const formatBulkSelectionLabel = useCallback(
    () => `${selectedCount} ${pluralize(selectedCount, 'Location')} Selected`,
    [selectedCount],
  );

  return (
    <TableContainer>
      <TableHeader
        title={
          <Typography variant='h6' whiteSpace='nowrap'>
            Choose Locations to Add to Submission Group
          </Typography>
        }
        disableTitleTypography
        actions={tableActions}
        bulkSelected={bulkSelectedItems.size}
        bulkActions={tableActions}
        formatBulkSelectionLabel={formatBulkSelectionLabel}
      />
      <SubmissionGroupLocationsTable
        cpg={cpg}
        loading={loading}
        data={data}
        error={error}
        paginationProps={paginationProps}
        paginationInfo={paginationInfo}
        onPageChange={onPageChange}
        bulkSelectedItems={bulkSelectedItems}
        handleBulkSelectMultiple={handleBulkSelectMultiple}
        handleItemSelect={handleItemSelect}
        handleBulkDeselectAll={handleBulkDeselectAll}
      />
    </TableContainer>
  );
};
